import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowUpRight, CreditCard, Repeat, ShoppingBag, Loader2, TrendingUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = {
  package: ShoppingBag,
  subscription: Repeat,
  guide: CreditCard,
};

const COLORS = {
  package: "bg-[var(--ink)] text-[var(--lime)]",
  subscription: "bg-[var(--lime)] text-[var(--ink)]",
  guide: "bg-[var(--mist-soft)] text-[var(--ink)]",
};

function timeAgo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.round(hr / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short" });
}

/**
 * Live sales feed — shows the last 20 sales across all products (Stripe + PayPal,
 * packages + subscriptions). Auto-refreshes every 30s.
 */
export const SalesFeed = () => {
  const [items, setItems] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const adminPw = localStorage.getItem("ditch_admin_pw") || "";
      const { data } = await axios.get(`${API}/receipts/admin/list?limit=20`, {
        headers: adminPw ? { "X-Admin-Password": adminPw } : {},
      });
      setItems(data.receipts || []);
    } catch (e) {
      setItems([]);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  if (items === null) {
    return (
      <div data-testid="sales-feed-loading" className="bg-white border border-[var(--line)] rounded-2xl p-6 mb-8">
        <Loader2 size={20} className="animate-spin text-[var(--ocean)]" />
      </div>
    );
  }

  // Aggregate today + 7-day totals (best-effort, by transaction's created_at)
  const now = Date.now();
  const ONE_DAY = 24 * 60 * 60 * 1000;
  const sumIf = (predicate) =>
    items
      .filter((r) => predicate(new Date(r.created_at).getTime()))
      .reduce((s, r) => s + Number(r.amount || 0), 0);
  const today = sumIf((t) => now - t < ONE_DAY);
  const week  = sumIf((t) => now - t < 7 * ONE_DAY);

  return (
    <div data-testid="sales-feed" className="bg-white border border-[var(--line)] rounded-2xl p-5 md:p-6 mb-8">
      <div className="flex items-start justify-between mb-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp size={16} className="text-[var(--ink)]" />
            <span className="text-[10px] font-bold uppercase tracking-widest text-[var(--ocean)]">
              Live activity
            </span>
            <span className="relative flex h-2 w-2 ml-1" aria-label="live">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--lime)] opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[var(--lime)]"></span>
            </span>
          </div>
          <h3 className="font-display text-xl font-extrabold text-[var(--ink)]">Recent sales</h3>
        </div>
        <div className="flex flex-col items-end gap-1 text-right">
          <div className="flex gap-5">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--ocean)]">Today</p>
              <p className="font-display text-xl font-extrabold text-[var(--ink)]">
                ${today.toLocaleString("en-AU", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </p>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--ocean)]">7-day</p>
              <p className="font-display text-xl font-extrabold text-[var(--ink)]">
                ${week.toLocaleString("en-AU", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </p>
            </div>
          </div>
          {refreshing && (
            <span className="text-[10px] text-[var(--ocean)] italic flex items-center gap-1">
              <Loader2 size={9} className="animate-spin" /> updating…
            </span>
          )}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-10">
          <p className="text-sm text-[var(--ocean)] italic">
            No sales yet — your first one will land here the second it happens.
          </p>
        </div>
      ) : (
        <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1 -mr-1">
          {items.map((r) => {
            const Icon = ICONS[r.product_type] || ShoppingBag;
            const buyerName = `${r.first_name || ""} ${r.last_name || ""}`.trim() || r.email;
            return (
              <a
                key={r.token}
                data-testid={`sales-feed-row-${r.receipt_number}`}
                href={r.token ? `${API}/receipts/${r.token}/file` : "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-3 p-3 rounded-xl hover:bg-[var(--mist-soft)] transition-colors border border-transparent hover:border-[var(--line)]"
              >
                <div
                  className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${
                    COLORS[r.product_type] || COLORS.guide
                  }`}
                >
                  <Icon size={15} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-3">
                    <p className="font-bold text-sm text-[var(--ink)] truncate">{buyerName}</p>
                    <p className="font-display text-base font-extrabold text-[var(--ink)] tabular-nums whitespace-nowrap">
                      ${Number(r.amount || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{" "}
                      <span className="text-[10px] text-[var(--ocean)] font-bold">{r.currency || "AUD"}</span>
                    </p>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-xs text-[var(--ocean)] truncate">{r.line_description}</p>
                    <p className="text-[11px] text-[var(--ocean)] italic whitespace-nowrap">
                      {timeAgo(r.created_at)} · {r.provider}
                    </p>
                  </div>
                </div>
                <ArrowUpRight
                  size={14}
                  className="flex-shrink-0 text-[var(--ocean)] group-hover:text-[var(--ink)] transition-colors"
                />
              </a>
            );
          })}
        </div>
      )}

      <p className="text-[10px] text-[var(--ocean)] italic text-center mt-4">
        Updates every 30 seconds · Click any row to open the receipt PDF
      </p>
    </div>
  );
};
